import { BLOCKS } from '@contentful/rich-text-types'
import CIOColors from '~/utils/color'
import { RichText } from '~/components/RichText'
import type { RichTextOptions } from '~/components/RichText/types'
import { IdeaIcon } from '~/components/icons'
import { TypeExpertTip } from '~/types/contentful'

type ExpertTipProps = {
  expertTip: TypeExpertTip<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

type BackgroundColor = 'gray' | 'green'

const richTextOptions: RichTextOptions = {
  extraProps: {
    [BLOCKS.PARAGRAPH]: {},
  },
  renderNode: {
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol className="list-none p-0 statistical-ol" style={{ counterReset: 'ol-counter' }}>
        {children}
      </ol>
    ),
    [BLOCKS.UL_LIST]: (_, children) => <ul className="list-disc pl-4 mb-0">{children}</ul>,
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <li className="relative pl-5 before:block before:absolute before:top-0 before:left-0 max-md:pl-3 before:content-none last:mb-2 mb-4 text-xs text-neutral-60">
        {children}
      </li>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => <p className="text-xs">{children}</p>,
  },
}

export const ExpertTip = ({ expertTip }: ExpertTipProps) => {
  const { fields } = expertTip
  const { title, description, body, backgroundColor = 'green' } = fields

  const bgColors: Record<BackgroundColor, { bgColor: string }> = {
    gray: {
      bgColor: CIOColors.neutral[5],
    },
    green: {
      bgColor: '#E7f4ED',
    },
  }

  const lowerCaseBackgroundColor = (backgroundColor.toLowerCase() || 'green') as BackgroundColor

  const { bgColor } = bgColors[lowerCaseBackgroundColor] || bgColors.green

  return (
    <div
      className="flex flex-col p-6 items-start self-stretch rounded-xl gap-6 my-12"
      style={{ backgroundColor: bgColor }}
    >
      <div className="flex flex-col items-start gap-3 self-stretch">
        <div className="flex gap-2 items-center self-stretch">
          <IdeaIcon
            className={`size-6 ${lowerCaseBackgroundColor === 'gray' ? 'text-blue-40' : 'text-green-60'}`}
          />
          <span className="text-m text-neutral-100">{title || 'Expert Tip'}</span>
        </div>
        <div className="flex px-8 justify-center items-center self-stretch">
          <p className="text-xs text-neutral-100">{description}</p>
        </div>
      </div>
      <RichText doc={body} options={richTextOptions} className="ExpertTip__RichText" />
    </div>
  )
}
